/**
 * SecuritiesCard (Patch BK): editable sub-card for non-Bitcoin tradable
 * positions. Renders inside the new "Other assets" parent card on the
 * Purchases page; same component instance powers both the Stocks & ETFs
 * sub-card and the Metals sub-card by passing different kindFilter
 * props.
 *
 * Per-asset structure mirrors EtfTransactions:
 *   - Header row with symbol/name, current price, position summary,
 *     delete button.
 *   - AutoBuyPanel for recurring buy/sell schedules (the panel handles
 *     'security' assetType natively after BJ widened it).
 *   - Transaction table: date, direction (Buy/Sell), quantity, price,
 *     value, note, delete. Inline-editable via DecimalInput.
 *   - Add-row at the bottom of each table.
 *   - Add-new-asset form at the bottom of the sub-card using
 *     TickerAutocomplete; commits both bundled and custom-typed
 *     tickers, validating via the price proxy on save.
 *
 * Spot metals (assetClass === 'spot_metal') get unit-aware display:
 * quantity is in grams, price is converted from Yahoo's USD/oz feed to
 * USD/gram (divide by GRAMS_PER_TROY_OZ). The math is otherwise identical
 * to share-based assets.
 */

import { useState, useEffect } from 'react';
import { useAppStore } from '../state/store.js';
import { Decimal, ZERO } from '../engine/money.js';
import { formatUsd } from './Money.js';
import { DecimalInput } from './DecimalInput.js';
import { TickerAutocomplete } from './TickerAutocomplete.js';
import { AutoBuyPanel } from './AutoBuyPanel.js';
import type { SecurityAsset, SecurityTransaction, SecurityAssetClass } from '../engine/types.js';
import type { BundledTicker } from '../data/tickers.js';
import { getCurrentSubdomain, isFIRETenant } from '../io/tenant.js';

/** Troy ounces to grams: standard precious-metal conversion. Yahoo's
 *  spot tickers (XAUUSD=X et al) report USD per troy ounce, so this
 *  divisor converts to per-gram for the spot_metal display unit. */
const GRAMS_PER_TROY_OZ = new Decimal('31.1034768');

interface SecuritiesCardProps {
  /** Which SecurityAssetClass values belong in this sub-card. Stocks &
   *  ETFs passes most non-metal kinds; Metals passes only the metal-
   *  related kinds. */
  kindFilter: SecurityAssetClass[];
  /** Sub-card title shown inside the disclosure summary. */
  title: string;
  /** Default assetClass to apply when a user picks a "Custom..." ticker
   *  that isn't in the bundled list. Stocks card defaults to 'stock',
   *  Metals card defaults to 'metal_etf' (closer to a typical user
   *  expectation than 'spot_metal'). */
  customDefaultKind: SecurityAssetClass;
  /** Optional hint shown beneath the title. */
  hint?: string;
}

const CATEGORY_LABELS: Record<SecurityTransaction['category'], string> = {
  starting_balance: 'Starting balance',
  salary: isFIRETenant() ? 'Business income' : 'Salary',
  bonus: 'Bonus',
  tax_refund: 'Tax refund',
  stock_sale: 'Stock sale',
  profit_share: 'Profit share',
  loan: 'Loan',
  manual: 'Manual',
  other: 'Other',
};

const SELECTABLE_CATEGORIES: Array<SecurityTransaction['category']> = [
  'manual', 'salary', 'bonus', 'tax_refund', 'stock_sale', 'profit_share', 'loan', 'other',
];

/** True when this asset's quantity is measured in grams, not shares.
 *  Drives the unit suffix in inputs + display + the oz-to-gram price
 *  conversion. */
function isGramUnit(asset: SecurityAsset): boolean {
  return asset.assetClass === 'spot_metal';
}

/** USD per unit (share or gram) for an asset. For spot metals, the
 *  underlying snapshot is USD/oz; we convert. For everything else,
 *  the snapshot is already USD/share. Manual override always wins. */
function unitPriceUsd(asset: SecurityAsset): Decimal | null {
  const raw = asset.manualPriceUsd ?? asset.livePriceSnapshot?.priceUsd ?? null;
  if (raw === null) return null;
  return isGramUnit(asset) ? raw.div(GRAMS_PER_TROY_OZ) : raw;
}

/** Sum buys - sum sells = current quantity holding. */
function currentQuantity(assetId: string, txs: SecurityTransaction[]): Decimal {
  let bal = ZERO;
  for (const t of txs) {
    if (t.assetId !== assetId) continue;
    bal = t.direction === 'buy' ? bal.plus(t.quantity) : bal.minus(t.quantity);
  }
  return bal;
}

interface NewRowState {
  date: string;
  direction: 'buy' | 'sell';
  quantity: string;
  pricePerUnit: string;
  category: SecurityTransaction['category'];
  note: string;
}

function defaultNewRow(): NewRowState {
  return {
    date: new Date().toLocaleDateString('sv-SE'),
    direction: 'buy',
    quantity: '',
    pricePerUnit: '',
    category: 'manual',
    note: '',
  };
}

export function SecuritiesCard(props: SecuritiesCardProps): JSX.Element {
  const allAssets = useAppStore(s => s.securityAssets);
  const allTxs = useAppStore(s => s.securityTransactions);
  const addSecurityAsset = useAppStore(s => s.addSecurityAsset);
  const updateSecurityAsset = useAppStore(s => s.updateSecurityAsset);
  const removeSecurityAsset = useAppStore(s => s.removeSecurityAsset);
  const addSecurityTransaction = useAppStore(s => s.addSecurityTransaction);
  const updateSecurityTransaction = useAppStore(s => s.updateSecurityTransaction);
  const removeSecurityTransaction = useAppStore(s => s.removeSecurityTransaction);

  // Filter assets to those in this sub-card's kind list. Assets are
  // owned globally; the filter is just for display routing.
  const assets = allAssets.filter(a => props.kindFilter.includes(a.assetClass));

  // Add-asset error state: surfaces "no such symbol" inline when a
  // custom ticker fails the price-proxy validation.
  const [addError, setAddError] = useState<string | null>(null);

  function commitBundled(t: BundledTicker): void {
    setAddError(null);
    addSecurityAsset({
      symbol: t.symbol,
      name: t.name,
      assetClass: t.kind,
    });
  }

  async function commitCustom(symbol: string): Promise<void> {
    setAddError(null);
    // Validate via the price proxy. If it returns a price, we have a
    // valid Yahoo ticker; persist and let addSecurityAsset's background
    // fetcher hydrate the snapshot. If not, surface the error inline.
    try {
      const { fetchEtfPrice } = await import('../io/live/etf-price.js');
      await fetchEtfPrice(symbol);
      addSecurityAsset({
        symbol,
        name: symbol,
        assetClass: props.customDefaultKind,
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setAddError(`No such symbol on Yahoo: ${symbol}. ${msg}`);
    }
  }

  return (
    <div>
      {/* Optional hint above the asset list. Kept minimal so it doesn't
          compete with content. */}
      {props.hint ? (
        <p className="muted" style={{ fontSize: 'var(--fs-small)', lineHeight: 1.6, marginTop: 0, marginBottom: 'var(--gap-m)' }}>
          {props.hint}
        </p>
      ) : null}

      {/* Per-asset blocks. Each asset gets its own section with header,
          AutoBuy panel, and transaction table. */}
      {assets.length === 0 ? (
        <p className="muted" style={{ fontStyle: 'italic', marginBottom: 'var(--gap-m)' }}>
          No {props.title.toLowerCase()} yet. Add one below.
        </p>
      ) : (
        assets.map(asset => (
          <AssetBlock
            key={asset.id}
            asset={asset}
            txs={allTxs.filter(t => t.assetId === asset.id)}
            onUpdateAsset={patch => updateSecurityAsset(asset.id, patch)}
            onRemoveAsset={() => {
              const txCount = allTxs.filter(t => t.assetId === asset.id).length;
              if (confirm(`Remove ${asset.symbol} and its ${txCount} transaction${txCount === 1 ? '' : 's'}? This is undoable from the header Undo button.`)) {
                removeSecurityAsset(asset.id);
              }
            }}
            onAddTx={tx => addSecurityTransaction(tx)}
            onUpdateTx={(id, patch) => updateSecurityTransaction(id, patch)}
            onRemoveTx={id => removeSecurityTransaction(id)}
          />
        ))
      )}

      {/* Add-new-asset form. Always visible at the bottom of the
          sub-card so adding the first asset doesn't require finding
          a hidden button. */}
      <div style={{
        marginTop: assets.length > 0 ? 'var(--gap-l)' : 'var(--gap-m)',
        paddingTop: 'var(--gap-m)',
        borderTop: assets.length > 0 ? '1px dashed var(--rule)' : 'none',
      }}>
        <div className="row" style={{ alignItems: 'center', gap: 'var(--gap-s)', flexWrap: 'wrap' }}>
          <span className="faint mono" style={{ fontSize: 'var(--fs-small)' }}>Add asset:</span>
          <TickerAutocomplete
            kindFilter={props.kindFilter}
            onSelect={commitBundled}
            onCustom={sym => void commitCustom(sym)}
            placeholder="Type ticker or name..."
          />
        </div>
        {addError ? (
          <div className="notice" style={{ marginTop: 'var(--gap-s)', borderLeftColor: 'var(--neg)' }}>
            {addError}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ---------- AssetBlock: header + autobuy + table for ONE asset ----------

interface AssetBlockProps {
  asset: SecurityAsset;
  txs: SecurityTransaction[];
  onUpdateAsset: (patch: Partial<SecurityAsset>) => void;
  onRemoveAsset: () => void;
  onAddTx: (tx: Omit<SecurityTransaction, 'id'>) => void;
  onUpdateTx: (id: string, patch: Partial<SecurityTransaction>) => void;
  onRemoveTx: (id: string) => void;
}

function AssetBlock(props: AssetBlockProps): JSX.Element {
  const { asset, txs, onUpdateAsset, onRemoveAsset, onAddTx, onUpdateTx, onRemoveTx } = props;
  const isGrams = isGramUnit(asset);
  const unitLabel = isGrams ? 'g' : 'sh';
  const unitColLabel = isGrams ? 'Grams' : 'Shares';
  const price = unitPriceUsd(asset);
  const quantity = currentQuantity(asset.id, txs);
  const value = price ? quantity.times(price) : null;

  const isStrc = asset.symbol.toUpperCase() === 'STRC';

  // Sync dividend reinvestment transactions when the checkbox is on.
  // Runs on mount (if already enabled) and whenever the flag toggles on.
  useEffect(() => {
    if (!isStrc || !asset.reinvestDividends) return;
    void syncDividends();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStrc, asset.reinvestDividends]);

  async function syncDividends(): Promise<void> {
    try {
      const res = await fetch(
        `/api/etf-dividend-history?symbol=${encodeURIComponent(asset.symbol)}`,
        { headers: { Accept: 'application/json' } },
      );
      if (!res.ok) return;
      const data = (await res.json()) as {
        dividends?: Array<{
          exDate: string;
          exDateMs: number;
          amountPerShare: number;
          closePrice: number | null;
        }>;
      };
      if (!Array.isArray(data.dividends)) return;

      // Build set of already-inserted keys so we never duplicate a row.
      const existingKeys = new Set(
        txs.filter(t => t.dividendKey).map(t => t.dividendKey!),
      );

      // Process in chronological order so DRIP shares compound correctly:
      // shares bought in month N earn dividends in month N+1.
      // We maintain a running list that includes newly created rows so that
      // each subsequent dividend sees the correct (compounded) balance.
      let runningTxs = [...txs];

      for (const div of data.dividends) {
        const key = `div:${asset.id}:${div.exDate}`;
        if (existingKeys.has(key)) continue;
        if (!div.closePrice) continue;

        // Shares held just before this ex-date (including previously
        // generated DRIP rows from earlier in this loop).
        const exDateMs = div.exDateMs;
        let sharesHeld = ZERO;
        for (const t of runningTxs) {
          if (t.date.getTime() >= exDateMs) continue;
          sharesHeld = t.direction === 'buy'
            ? sharesHeld.plus(t.quantity)
            : sharesHeld.minus(t.quantity);
        }
        if (sharesHeld.lte(ZERO)) continue;

        const cash = sharesHeld.times(new Decimal(div.amountPerShare));
        const sharesReinvested = cash.div(new Decimal(div.closePrice));
        if (sharesReinvested.lte(ZERO)) continue;

        const newTx: Omit<SecurityTransaction, 'id'> = {
          assetId: asset.id,
          date: new Date(exDateMs),
          direction: 'buy',
          quantity: sharesReinvested,
          pricePerUnitUsd: new Decimal(div.closePrice),
          category: 'other',
          note: `DRIP ${div.amountPerShare.toFixed(4)}/sh`,
          autoCreated: true,
          dividendKey: key,
        };
        onAddTx(newTx);
        existingKeys.add(key);
        // Add a stub to runningTxs so later dividends see compounded shares.
        runningTxs = [...runningTxs, { ...newTx, id: `tmp:${key}` }];
      }
    } catch {
      // Silent  -  dividend sync is best-effort; user can always add manually.
    }
  }

  const sortedTxs = [...txs].sort((a, b) => a.date.getTime() - b.date.getTime());

  const [newRow, setNewRow] = useState<NewRowState>(defaultNewRow);

  function addRow(): void {
    const qty = parseFloat(newRow.quantity);
    if (!isFinite(qty) || qty <= 0) {
      alert('Enter a positive quantity (shares or grams).');
      return;
    }
    const ppu = parseFloat(newRow.pricePerUnit);
    const tx: Omit<SecurityTransaction, 'id'> = {
      assetId: asset.id,
      date: new Date(newRow.date + 'T12:00:00Z'),
      direction: newRow.direction,
      quantity: new Decimal(qty),
      category: newRow.category,
    };
    if (isFinite(ppu) && ppu > 0) {
      tx.pricePerUnitUsd = new Decimal(ppu);
    }
    if (newRow.note.trim() !== '') {
      tx.note = newRow.note.trim();
    }
    onAddTx(tx);
    setNewRow(defaultNewRow());
  }

  return (
    <div style={{ marginBottom: 'var(--gap-l)' }}>
      {/* Asset header: symbol/name on the left, position summary in the
          middle, delete on the right. */}
      <div className="row between" style={{ alignItems: 'baseline', marginBottom: 'var(--gap-s)', flexWrap: 'wrap', gap: 'var(--gap-s)' }}>
        <div>
          <span className="mono" style={{ fontWeight: 600, fontSize: 'var(--fs-base)' }}>
            {asset.symbol}
          </span>
          <span className="faint" style={{ marginLeft: 'var(--gap-s)', fontSize: 'var(--fs-small)' }}>
            {asset.name}
          </span>
        </div>
        <div className="row" style={{ gap: 'var(--gap-m)', alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span className="mono" style={{ fontSize: 'var(--fs-small)' }}>
            <span className="faint">price{isGrams ? '/g' : ''}: </span>
            {price ? `$${formatUsd(price, { decimals: 2 })}` : <span className="faint">loading...</span>}
          </span>
          <span className="mono" style={{ fontSize: 'var(--fs-small)' }}>
            <span className="faint">balance: </span>
            {quantity.toFixed(isGrams ? 2 : 4)} {unitLabel}
          </span>
          <span className="mono" style={{ fontSize: 'var(--fs-small)' }}>
            <span className="faint">value: </span>
            {value !== null
              ? <span style={{ color: 'var(--accent)' }}>${formatUsd(value, { decimals: 0 })}</span>
              : <span className="faint">-</span>}
          </span>
          <button
            className="ghost"
            onClick={onRemoveAsset}
            style={{ padding: '2px 8px', fontSize: 'var(--fs-micro)', color: 'var(--text-soft)' }}
            title={`Drop ${asset.symbol} and its transactions`}
          >
            remove
          </button>
        </div>
      </div>

      {/* STRC-only: reinvest dividends checkbox. Syncs DRIP transactions
          silently in the background when enabled. */}
      {isStrc ? (
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 'var(--fs-small)', color: 'var(--text-soft)', marginBottom: 'var(--gap-s)' }}>
          <input
            type="checkbox"
            checked={asset.reinvestDividends ?? false}
            onChange={e => onUpdateAsset({ reinvestDividends: e.target.checked })}
          />
          Reinvest dividends
        </label>
      ) : null}

      {/* AutoBuy panel, same component, with assetType='security' so
          the walker (post-BK in a later patch) will emit
          SecurityTransactions on schedule. For now (BK) the walker
          short-circuits on assetType==='security', so enabling a
          schedule here is durable but won't emit rows until the BK+1
          patch adds walker support. Surfacing the panel here lets users
          configure schedules ahead of that arrival. */}
      <AutoBuyPanel
        assetType="security"
        assetId={asset.id}
        assetLabel={`${asset.symbol} (${asset.name})`}
        assetHint={isGrams ? 'Quantity in grams; auto-sell available' : 'Auto-sell available'}
      />

      {/* Transaction table. Same column layout the ETF transactions use,
          plus a Direction column (Buy/Sell) right after Date. */}
      {sortedTxs.length > 0 ? (
        <table className="tbl" style={{ marginTop: 'var(--gap-s)' }}>
          <thead>
            <tr>
              <th>Date</th>
              <th>Dir</th>
              <th className="num">{unitColLabel}</th>
              <th className="num">Price{isGrams ? '/g' : '/sh'}</th>
              <th className="num">Value</th>
              <th>Category</th>
              <th>Note</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sortedTxs.map(t => {
              const txValue = t.pricePerUnitUsd ? t.quantity.times(t.pricePerUnitUsd) : null;
              return (
                <tr key={t.id}>
                  <td>
                    <input
                      type="date"
                      value={t.date.toISOString().slice(0, 10)}
                      onChange={e => {
                        if (e.target.value) {
                          onUpdateTx(t.id, { date: new Date(e.target.value + 'T12:00:00Z') });
                        }
                      }}
                      style={{ width: 130, fontSize: 'var(--fs-small)' }}
                    />
                  </td>
                  <td>
                    <select
                      value={t.direction}
                      onChange={e => onUpdateTx(t.id, { direction: e.target.value as 'buy' | 'sell' })}
                      style={{ fontSize: 'var(--fs-small)' }}
                    >
                      <option value="buy">Buy</option>
                      <option value="sell">Sell</option>
                    </select>
                  </td>
                  <td className="num">
                    <DecimalInput
                      decimals={isGrams ? 2 : 4}
                      step={isGrams ? '0.1' : '0.01'}
                      value={t.quantity}
                      onChange={d => onUpdateTx(t.id, { quantity: d })}
                      style={{ width: 80 }}
                    />
                  </td>
                  <td className="num">
                    <DecimalInput
                      decimals={2}
                      step="0.01"
                      value={t.pricePerUnitUsd ?? ZERO}
                      onChange={d => onUpdateTx(t.id, { pricePerUnitUsd: d })}
                      style={{ width: 90 }}
                    />
                  </td>
                  <td className="num mono">
                    {txValue ? `$${formatUsd(txValue, { decimals: 0 })}` : <span className="faint">-</span>}
                  </td>
                  <td>
                    <select
                      value={t.category}
                      onChange={e => onUpdateTx(t.id, { category: e.target.value as SecurityTransaction['category'] })}
                      style={{ fontSize: 'var(--fs-small)' }}
                    >
                      {t.category === 'starting_balance' ? (
                        // Legacy display only; never re-add to the selectable list.
                        <option value="starting_balance">{CATEGORY_LABELS.starting_balance}</option>
                      ) : null}
                      {SELECTABLE_CATEGORIES.map(c => (
                        <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      type="text"
                      value={t.note ?? ''}
                      onChange={e => onUpdateTx(t.id, { note: e.target.value })}
                      style={{ width: 120, fontSize: 'var(--fs-small)' }}
                    />
                    {t.autoCreated ? (
                      <span className="mono faint" style={{ marginLeft: 4, fontSize: 'var(--fs-micro)', color: 'var(--accent)' }}>
                        AUTO
                      </span>
                    ) : null}
                  </td>
                  <td>
                    <button
                      className="ghost"
                      onClick={() => onRemoveTx(t.id)}
                      style={{ padding: '2px 6px', fontSize: 'var(--fs-micro)', color: 'var(--text-soft)' }}
                      title="Delete this transaction"
                    >
                      del
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}

      {/* Add-transaction row. Inline form just below the table. */}
      <div className="row" style={{ alignItems: 'center', gap: 'var(--gap-s)', flexWrap: 'wrap', marginTop: 'var(--gap-s)' }}>
        <input
          type="date"
          value={newRow.date}
          onChange={e => setNewRow({ ...newRow, date: e.target.value })}
          style={{ width: 130, fontSize: 'var(--fs-small)' }}
        />
        <select
          value={newRow.direction}
          onChange={e => setNewRow({ ...newRow, direction: e.target.value as 'buy' | 'sell' })}
          style={{ fontSize: 'var(--fs-small)' }}
        >
          <option value="buy">Buy</option>
          <option value="sell">Sell</option>
        </select>
        <input
          type="number"
          step={isGrams ? '0.1' : '0.01'}
          placeholder={isGrams ? 'grams' : 'shares'}
          value={newRow.quantity}
          onChange={e => setNewRow({ ...newRow, quantity: e.target.value })}
          style={{ width: 90, fontSize: 'var(--fs-small)' }}
        />
        <input
          type="number"
          step="0.01"
          placeholder={`price${isGrams ? '/g' : '/sh'}`}
          value={newRow.pricePerUnit}
          onChange={e => setNewRow({ ...newRow, pricePerUnit: e.target.value })}
          style={{ width: 90, fontSize: 'var(--fs-small)' }}
        />
        <select
          value={newRow.category}
          onChange={e => setNewRow({ ...newRow, category: e.target.value as SecurityTransaction['category'] })}
          style={{ fontSize: 'var(--fs-small)' }}
        >
          {SELECTABLE_CATEGORIES.map(c => (
            <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="note (optional)"
          value={newRow.note}
          onChange={e => setNewRow({ ...newRow, note: e.target.value })}
          style={{ width: 140, fontSize: 'var(--fs-small)' }}
        />
        <button onClick={addRow} style={{ fontSize: 'var(--fs-small)' }}>
          Add transaction
        </button>
      </div>
    </div>
  );
}
